import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { switchMap, take, tap } from 'rxjs';

import { HomeFlightOffers } from '@shared/home-sections/entities/home-data.response';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { mapSearchData } from '@search-bar/mappers/url-search.mapper';
import { flightResultsRootRoute } from '@flight-results/flight-results-routing.constants';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { FlightType } from '@search-bar/entities/flight-type';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { isNullOrUndefined } from '@app/util/util';
import { AvoService } from '@avo/services/avo.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';

const ilusTravel = 'ilus-travel.png';
const UTC = 'UTC';
const FLIGHT_SCALE = 'OFFERS_FLIGHT.FLIGHT_SCALE';
const FLIGHT_SCALES = 'OFFERS_FLIGHT.FLIGHT_SCALES';
@Component({
  selector: 'app-slide-flight-offers',
  templateUrl: './slide-flight-offers.component.html',
  styleUrls: ['./slide-flight-offers.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SlideFlightOffersComponent {

  @Input() flightOffers: HomeFlightOffers[] = [];
  @Input() index: number;
  @Input() isMobile: boolean;
  @Input() departurePlace: AutocompletePlace;
  @Input() arrivalPlace: SelectAutocompletePlace;
  @Input() airline: string;
  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.TAG;

  @Output() editAction: EventEmitter<void> = new EventEmitter();

  private dateFormat: string = 'd MMM';

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  public readonly icons = Icon;
  public readonly iconColor = IconFillColor;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly datePipe: DatePipe,
    private readonly dateTransformPipe: DateTransformPipe,
    private readonly router: Router,
    private readonly propertiesAmplitudeService: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
  ) { }

  get ilusTravel(): string {
    return ilusTravel;
  }

  public isOneWay(item: HomeFlightOffers): boolean {
    return isNullOrUndefined(item.inbound);
  }

  public formatDate(date: string): string {
    return this.formatStringDate(this.datePipe.transform(new Date(date), this.dateFormat, UTC));
  }

  public formatStringDate(date: string): string {
    return this.dateTransformPipe.transform(date);
  }

  public redirecTo(item: HomeFlightOffers): void {
    this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(
      take(1),
      switchMap((searchProps) => this.avoService.getEventTriggerProps$(
        AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: this.source }, searchProps
      ).pipe(
        take(1),
        tap((trigger) => {
          this.editAction.emit();
          this.AvoDispatch.selectTravelSearch({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.avoService.getAvoSourceValueType(this.source),
          });
          const flightType: FlightType = this.isOneWay(item) ? FlightType.ONEWAY : FlightType.ROUNDTRIP;
          const payload = {
            flightType: flightType,
            departureDate: this.getFormatDate(item.outbound),
            arrivalDate: this.getFormatDate(item.inbound),
            destinationPlace: { code: item.arrival },
            arrivalPlace: { code: item.arrival },
            departurePlace: { code: item.departure },
            passengerArrangement: [
              { id: PassengerType.ADULTS, quantity: 1 },
              { id: PassengerType.CHILDREN, quantity: 0 },
              { id: PassengerType.INFANTS, quantity: 0 }],
            baggageIncluded: false,
            airline: this.airline,
          } as SearchData;
          const urlParams = mapSearchData(payload);
          this.router.navigate([`${flightResultsRootRoute}/${urlParams}`]);
        })
      ))
    ).subscribe().unsubscribe();
  }

  public isDirectFlight(stops: number): boolean {
    return stops === 0;
  }

  public flightWithScale(stops: number): string {
    return stops > 1 ? FLIGHT_SCALES : FLIGHT_SCALE;
  }

  private getFormatDate(date: string | null): Date | null {
    if (!date) {
      return null;
    }
    const TIME_ZONE_CONST = 'T';
    const timeZone = date.search(TIME_ZONE_CONST);
    const dateFormatString = timeZone > 0 ? date.substring(0, timeZone) : date;
    return formatDateByRegex(dateFormatString);
  }
}
